import { useCallback, useEffect, useState } from 'react';
import { Rng, STATE_FORMAT_VERSION } from '@oz/rules';
import { AuthProvider, useAuth } from './auth/AuthContext.js';
import { AuthModal } from './components/AuthModal.js';
import { HeaderNav } from './components/HeaderNav.js';
import { pushTab, replaceTab, tabFromHash, type AppTab } from './nav/route.js';
import { AssetBrowser } from './pages/AssetBrowser.js';
import { CampaignPage, type Chapter } from './pages/CampaignPage.js';
import { CommunityPage } from './pages/CommunityPage.js';
import { HelpChat } from './components/HelpChat.js';
import { LobbyPage, type Lobby } from './pages/LobbyPage.js';
import { ShopPage } from './pages/ShopPage.js';
import { MapEditor } from './pages/MapEditor.js';
import { Skirmish } from './pages/Skirmish.js';
import { AdminShell } from './pages/admin/AdminShell.js';
import type { SaveGame } from './game/saves.js';
import type { MapData } from './render/tilemap.js';

interface ApiHealth {
  readonly service: string;
  readonly status: string;
  readonly php: string;
  readonly database: { readonly connected: boolean; readonly server?: string; readonly error?: string };
}

type Probe = { state: 'carregando' } | { state: 'ok'; data: ApiHealth } | { state: 'erro'; message: string };

function MainApp(): JSX.Element {
  const { user } = useAuth();
  // A aba vem do endereço, não de um padrão fixo: quem recarrega em cima do
  // editor de mapas volta para o editor de mapas. Quem volta do Mercado Pago
  // (`/?pagamento=…&ref=…`, ver MercadoPago::createPreference) cai na Loja, que
  // é quem confere a compra — abrir na Escaramuça deixava a confirmação parada.
  const [activeTab, setActiveTab] = useState<AppTab>(() =>
    new URLSearchParams(window.location.search).has('pagamento')
      ? 'shop'
      : tabFromHash(window.location.hash),
  );
  const [skirmishMapId, setSkirmishMapId] = useState<number | undefined>(undefined);
  // Mapa em edição que ainda não foi gravado — o editor pode mandá-lo direto
  // para a escaramuça sem passar pelo banco.
  const [skirmishDraft, setSkirmishDraft] = useState<MapData | undefined>(undefined);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  /*
   * O capítulo em curso, quando a batalha veio do Modo História.
   *
   * A escaramuça não sabe o que é uma campanha — ela sabe jogar um mapa. O que
   * a liga à história é este pedaço de contexto: qual capítulo, para que ao
   * terminar o resultado volte ao servidor e a recompensa seja paga.
   */
  const [storyChapter, setStoryChapter] = useState<Chapter | null>(null);
  /** A sala, quando a batalha veio do lobby online. */
  const [onlineLobby, setOnlineLobby] = useState<Lobby | null>(null);
  /** A batalha guardada que a lista de capítulos mandou retomar, quando mandou. */
  const [resumeSave, setResumeSave] = useState<SaveGame | null>(null);
  const [api, setApi] = useState<Probe>({ state: 'carregando' });

  /**
   * Trocar de aba é navegar: entra no histórico e o endereço acompanha.
   *
   * Sem isto o botão Voltar do navegador saía do site inteiro no primeiro
   * clique, mesmo depois de a pessoa ter passado por cinco abas.
   */
  const goToTab = useCallback((tab: AppTab) => {
    setActiveTab(tab);
    pushTab(tab);
  }, []);

  // Voltar e avançar no navegador mudam a aba, sem recarregar a página.
  useEffect(() => {
    replaceTab(activeTab);
    const sync = (): void => setActiveTab(tabFromHash(window.location.hash));
    window.addEventListener('popstate', sync);
    window.addEventListener('hashchange', sync);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener('hashchange', sync);
    };
    // Só na montagem: `replaceTab` aqui serve para carimbar no endereço a aba
    // com que a página abriu, e repeti-lo a cada troca desfaria o histórico
    // que `goToTab` acabou de criar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/health', { signal: controller.signal })
      .then(async (r) => {
        const data = (await r.json()) as ApiHealth;
        if (r.ok || r.status === 503) return data;
        throw new Error(`HTTP ${r.status}`);
      })
      .then((data: ApiHealth) => setApi({ state: 'ok', data }))
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        setApi({ state: 'erro', message: e instanceof Error ? e.message : String(e) });
      });
    return () => controller.abort();
  }, []);

  const rng = new Rng(20260908);
  const rolls = Array.from({ length: 5 }, () => rng.range(1, 100));

  return (
    <div className="app-layout">
      <HeaderNav
        activeTab={activeTab}
        onTabChange={(tab) => {
          // Escaramuça escolhida no menu é escaramuça: sem isto, o capítulo ou a
          // sala da última batalha continuavam presos à aba, e a partida nova
          // mandava o desfecho de novo para a campanha ou para a sala.
          if (tab === 'skirmish') {
            setStoryChapter(null);
            setOnlineLobby(null);
            setSkirmishDraft(undefined);
            setResumeSave(null);
            // E o mapa da última batalha também sai: com ele preso à aba, a
            // Escaramuça abria direto num tabuleiro em vez de na tela inicial.
            setSkirmishMapId(undefined);
          }
          goToTab(tab);
        }}
        onOpenAuth={() => setAuthModalOpen(true)}
      />

      {/* O editor de mapas e o painel administrativo governam a própria altura e
          querem a tela inteira; as demais abas usam o container central. */}
      <main
        className={
          activeTab === 'maps' || activeTab === 'admin'
            ? 'app-main-content full-bleed'
            : 'app-main-content'
        }
      >
        {activeTab === 'skirmish' && (
          <Skirmish
            initialMapId={skirmishMapId}
            draftMap={skirmishDraft}
            storyChapter={storyChapter}
            onlineLobby={onlineLobby}
            resumeSave={resumeSave}
            onExitToCampaign={() => {
              setStoryChapter(null);
              setSkirmishMapId(undefined);
              setResumeSave(null);
              goToTab('campaign');
            }}
          />
        )}
        {activeTab === 'campaign' && (
          <CampaignPage
            onOpenAuth={() => setAuthModalOpen(true)}
            onPlayChapter={(chapter, campaignSlug, resume) => {
              setSkirmishDraft(undefined);
              setOnlineLobby(null);
              setResumeSave(resume ?? null);
              // O apelido da campanha viaja com o capítulo: é por ele que a
              // batalha encontra o capítulo SEGUINTE quando o jogador vence.
              setStoryChapter({ ...chapter, campaignSlug });
              setSkirmishMapId(chapter.mapId);
              goToTab('skirmish');
            }}
          />
        )}
        {activeTab === 'online' && (
          <LobbyPage
            onOpenAuth={() => setAuthModalOpen(true)}
            onPlayLobby={(lobby) => {
              setSkirmishDraft(undefined);
              setStoryChapter(null);
              setResumeSave(null);
              setOnlineLobby(lobby);
              setSkirmishMapId(lobby.mapId);
              goToTab('skirmish');
            }}
          />
        )}
        {activeTab === 'shop' && <ShopPage onOpenAuth={() => setAuthModalOpen(true)} />}
        {activeTab === 'community' && (
          <CommunityPage onOpenAuth={() => setAuthModalOpen(true)} />
        )}
        {activeTab === 'maps' && (
          !user ? (
            <div className="auth-locked-view">
              <div className="admin-locked">
                <span className="admin-locked-icon">🗺️</span>
                <h2>Criação de Mapas Bloqueada</h2>
                <p>
                  O editor de mapas exige login. Entre na sua conta para criar, editar ou testar mapas personalizados.
                </p>
                <button
                  type="button"
                  className="auth-open-btn large"
                  style={{ marginTop: '0.8rem' }}
                  onClick={() => setAuthModalOpen(true)}
                >
                  🔑 Entrar / Cadastrar
                </button>
              </div>
            </div>
          ) : (
            <MapEditor
              onPlayMap={(id) => {
                setSkirmishDraft(undefined);
                setStoryChapter(null);
                setOnlineLobby(null);
                setResumeSave(null);
                setSkirmishMapId(id);
                goToTab('skirmish');
              }}
              onPlayDraft={(map) => {
                setSkirmishDraft(map);
                setStoryChapter(null);
                setResumeSave(null);
                setOnlineLobby(null);
                setSkirmishMapId(map.id || undefined);
                goToTab('skirmish');
              }}
            />
          )
        )}
        {activeTab === 'assets' && (
          !user ? (
            <div className="auth-locked-view">
              <div className="admin-locked">
                <span className="admin-locked-icon">🎨</span>
                <h2>Acervo Bloqueado</h2>
                <p>
                  O acervo de recursos exige login. Entre na sua conta para explorar a galeria de artes, sons e animações.
                </p>
                <button
                  type="button"
                  className="auth-open-btn large"
                  style={{ marginTop: '0.8rem' }}
                  onClick={() => setAuthModalOpen(true)}
                >
                  🔑 Entrar / Cadastrar
                </button>
              </div>
            </div>
          ) : (
            <AssetBrowser />
          )
        )}
        {activeTab === 'admin' && <AdminShell />}
      </main>

      <footer className="app-system-footer">
        <details>
          <summary>Diagnóstico do Sistema (Motor, PHP & MariaDB)</summary>
          <div className="diagnostics-grid">
            <div>
              <strong>Motor de Regras</strong>
              <p className="muted">@oz/rules · Estado v{STATE_FORMAT_VERSION}</p>
              <p className="mono">Semente 20260908: {rolls.join(' · ')}</p>
            </div>
            <div>
              <strong>API Backend</strong>
              {api.state === 'carregando' && <p className="muted">consultando /api/health…</p>}
              {api.state === 'erro' && <p className="bad">Erro: {api.message}</p>}
              {api.state === 'ok' && (
                <p>
                  {api.data.service} ({api.data.status}) · PHP {api.data.php}
                </p>
              )}
            </div>
            <div>
              <strong>Banco de Dados</strong>
              {api.state === 'ok' && (
                <p className={api.data.database.connected ? 'good' : 'bad'}>
                  {api.data.database.connected
                    ? `MariaDB ${api.data.database.server}`
                    : (api.data.database.error ?? 'desconectado')}
                </p>
              )}
            </div>
          </div>
        </details>
      </footer>

      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} />

      {/* O ajudante fica sobre todas as abas — dúvida não escolhe hora. Ele
          mesmo se esconde quando o assistente não está configurado. */}
      <HelpChat />
    </div>
  );
}

export function App(): JSX.Element {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
}
